"use client";

// ЕДИНЫЙ источник цвета отдела для всего портала: «Команда»/«Управление
// пользователями», HR (чипы, график отпусков, оргструктура), запрос на доработку.
//
// Раньше палитра была скопирована трижды (user-management, dev-request-modal,
// hr-ui) и HR вдобавок красил отделы ПО СВОЕМУ списку названий — из-за этого один
// и тот же отдел на «Команде» и в графике отпусков был разного цвета.
//
// Приоритет: цвет, заданный в карточке отдела («Управление пользователями» →
// отдел → цвет) → иначе цвет по позиции отдела в списке из общей палитры →
// иначе (отдела в портале нет, например «Финансы» или «Склад» из старых карточек
// сотрудников) устойчивый цвет по хешу названия.

import { useCallback, useEffect, useState } from "react";

import { api } from "./api";
import type { Department } from "./types";

/** Общая палитра отделов (была AVATAR_COLORS в user-management). */
export const DEPT_PALETTE = ["#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#0EA5E9", "#6366F1", "#14B8A6", "#F97316"];

/** Название/ключ отдела к сравнимому виду: регистр, пробелы и знаки не важны. */
const norm = (s: string) => (s || "").toLowerCase().replace(/[^a-zа-яё0-9]+/gi, "");
/** Разнобой в написании: один и тот же отдел (ключ `rd`) в карточках HR записан
 *  строкой — «RnD», а по орг-схеме доски «Снабжение». В справочнике портала он
 *  называется «RnD» (Максим, 01.09.2026; до этого — «R&D»). */
const ALIASES: Record<string, string> = { rnd: "rd", рнд: "rd", снабжение: "rd", отделснабжения: "rd", ит: "it", маркетинг: "marketing" };
const canon = (s: string) => { const n = norm(s); return ALIASES[n] ?? n; };

function hashColor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return DEPT_PALETTE[h % DEPT_PALETTE.length];
}

/**
 * Отдел портала по ключу ИЛИ по названию из карточки сотрудника.
 *
 * ЕДИНОЕ место сопоставления «строка → отдел». Раньше каждый компонент писал
 * свой `norm` и сравнивал названия напрямую, мимо ALIASES, — и один и тот же
 * отдел показывался дважды: на орг. схеме «Снабжение» стояло отдельной
 * карточкой рядом с самим отделом (Максим, 27.08.2026).
 *
 * ⚠️ СНАЧАЛА ТОЧНОЕ СОВПАДЕНИЕ, разнобой в написании — только если точного нет.
 * ALIASES фиксируют факт о компании («Снабжение» — прежний RnD), а факты
 * меняются: если в справочнике заведут ОБА отдела, склеивать их нельзя.
 */
export function findDept(departments: Department[], keyOrTitle: string): Department | null {
  if (!keyOrTitle) return null;
  const n = norm(keyOrTitle);
  const exact = departments.find((d) => norm(d.key) === n || norm(d.title) === n);
  if (exact) return exact;
  const c = ALIASES[n] ?? n;
  return departments.find((d) => canon(d.key) === c || canon(d.title) === c) ?? null;
}

/** Цвет отдела по его ключу ИЛИ названию (сравнение с нормализацией). */
export function deptColorOf(departments: Department[], keyOrTitle: string): string {
  if (!keyOrTitle) return "#94a3b8";
  const d = findDept(departments, keyOrTitle);
  if (!d) return hashColor(keyOrTitle);
  return d.color || DEPT_PALETTE[departments.indexOf(d) % DEPT_PALETTE.length];
}

// ── Отделы для компонентов, которые их сами не грузят ────────────────────────
// Кэш на модуль: список отделов нужен многим местам, но меняется редко —
// грузим один раз на вкладку и раздаём всем подписчикам.
let cached: Department[] | null = null;
let inflight: Promise<Department[]> | null = null;
const subscribers = new Set<(d: Department[]) => void>();

function loadDepartments(): Promise<Department[]> {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = api
      .getDepartments()
      .then((list) => {
        cached = list;
        subscribers.forEach((fn) => fn(list));
        return list;
      })
      .catch(() => [])
      .finally(() => { inflight = null; });
  }
  return inflight;
}

/**
 * Резолвер цвета отдела для компонентов, у которых списка отделов нет под рукой
 * (HR берёт отдел строкой из карточки сотрудника). До загрузки отдаёт запасной
 * цвет по хешу — после она перерисует компонент уже портальным цветом.
 */
/** Справочник отделов для компонентов, которые его сами не грузят. */
export function useDepartments(): Department[] {
  const [depts, setDepts] = useState<Department[]>(cached ?? []);
  useEffect(() => {
    let alive = true;
    const onLoad = (list: Department[]) => { if (alive) setDepts(list); };
    subscribers.add(onLoad);
    void loadDepartments().then(onLoad);
    return () => { alive = false; subscribers.delete(onLoad); };
  }, []);
  return depts;
}

export function useDeptColor(): (keyOrTitle: string) => string {
  const [depts, setDepts] = useState<Department[]>(cached ?? []);
  useEffect(() => {
    let alive = true;
    const onLoad = (list: Department[]) => { if (alive) setDepts(list); };
    subscribers.add(onLoad);
    void loadDepartments().then(onLoad);
    return () => { alive = false; subscribers.delete(onLoad); };
  }, []);
  // ⚠️ useCallback обязателен: резолвер уходит в зависимости useMemo (оргструктура
  // пересобирает дерево), и новая функция на каждый рендер давала бесконечный цикл
  // «новое дерево → эффект → setState → новый рендер» (Maximum update depth exceeded).
  return useCallback((keyOrTitle: string) => deptColorOf(depts, keyOrTitle), [depts]);
}
